// `@primer-io/checkout-web` ships as CommonJS — a static import breaks Nuxt's
// prerender step. Dynamic import creates a lazy chunk; the Primer integration
// calls this early (on email set) so the chunk is cached by the time
// PrimerCheckout mounts.
//
// Failure handling: a rejected load is never cached — the singleton resets so
// every later call (checkout mount, reinit, the integration's online retry)
// re-attempts. A visitor who goes offline mid-pipe fails the chunk fetch the
// moment their email is set; caching that rejection would keep the checkout
// dead until a full page reload. Retrying the import alone is not enough
// either: Chrome and Firefox cache FAILED module fetches in the page's module
// map (fixed in the HTML spec 2026-07, whatwg/html#10327, not yet shipped in
// any engine), so a retried import() of the same chunk rejects instantly
// without touching the network. Script tags are exempt from that cache, so
// when the import fails we fall back to Primer's CDN build (`Primer.min.js`,
// same version), which exposes the same namespace on `window.Primer`.
//
// Version resolved at build time by Vite — no runtime package.json read needed.
import primerPkg from '@primer-io/checkout-web/package.json'
import { normalizeSdkVersion } from '@render/integrations/checkoutSdkVersion'

// The subset of the npm module the app consumes (see usePrimer.ts). The CDN
// fallback can only provide this surface, so the exported type is narrowed to
// keep both load paths honest.
export type PrimerSdk = Pick<typeof import('@primer-io/checkout-web'), 'Primer' | 'PaymentFlow'>

/** What every checkout loads unless the CHECKOUT integration sets another version. */
export const PRIMER_DEFAULT_SDK_VERSION = primerPkg.version

const cdnBase = (version: string) => `https://sdk.primer.io/web/v${version}`

// Set by the PRIMER integration's setup(), which a build runs synchronously in
// pipe.initialize() — before any page, and so any checkout, mounts. Undefined
// loads the bundled version.
let configuredVersion: string | undefined

export const configurePrimerSdk = (sdkVersion: unknown): void => {
  const version = normalizeSdkVersion('primer', sdkVersion)
  configuredVersion = version === PRIMER_DEFAULT_SDK_VERSION ? undefined : version
}

/** CDN folder of the version this page loads — what the integration pre-warms. */
export const primerCdnBase = (): string => cdnBase(configuredVersion ?? PRIMER_DEFAULT_SDK_VERSION)

// `window.Primer` as set by the CDN build — the same namespace surface as the
// npm module's `Primer` export (Primer.min.js ends with `window.Primer=n`
// where `n` carries showUniversalCheckout / createHeadless / ...).
type PrimerGlobal = PrimerSdk['Primer']

// PaymentFlow is a plain string enum in the npm package (dist/index.d.ts);
// the CDN build doesn't export it, so mirror its members for the fallback.
/* eslint-disable @typescript-eslint/naming-convention -- mirrors Primer's enum members */
const PAYMENT_FLOW_FALLBACK = {
  DEFAULT: 'DEFAULT',
  PREFER_VAULT: 'PREFER_VAULT',
} as PrimerSdk['PaymentFlow']
/* eslint-enable @typescript-eslint/naming-convention */

let primerSdkPromise: Promise<PrimerSdk> | null = null

const getGlobalPrimer = (): PrimerGlobal | null => {
  const g = (window as { Primer?: PrimerGlobal }).Primer
  return g && typeof g.showUniversalCheckout === 'function' ? g : null
}

const loadFromCdn = (base: string): Promise<PrimerSdk> =>
  new Promise<PrimerSdk>((resolve, reject) => {
    // A previous fallback attempt (or anything else) may have already put the
    // SDK on the global — no second script tag needed then.
    const fromGlobal = getGlobalPrimer()
    if (fromGlobal) {
      resolve({ Primer: fromGlobal, PaymentFlow: PAYMENT_FLOW_FALLBACK })
      return
    }
    const script = document.createElement('script')
    script.src = `${base}/Primer.min.js`
    script.crossOrigin = 'anonymous'
    script.onload = () => {
      const primer = getGlobalPrimer()
      if (primer) {
        resolve({ Primer: primer, PaymentFlow: PAYMENT_FLOW_FALLBACK })
        return
      }
      script.remove()
      reject(new Error('Primer CDN script loaded but window.Primer is missing'))
    }
    script.onerror = () => {
      // Drop the failed tag so a retry injects a fresh one instead of
      // stacking dead elements.
      script.remove()
      reject(new Error(`Failed to load Primer SDK from CDN: ${script.src}`))
    }
    document.head.appendChild(script)
  })

const loadBundled = (): Promise<PrimerSdk> =>
  import('@primer-io/checkout-web').catch((importErr) => {
    // SSR has no DOM for the fallback (and no module-map poisoning either).
    if (typeof window === 'undefined') throw importErr
    console.warn(
      '[primer] SDK chunk import failed — falling back to the Primer CDN script',
      importErr,
    )
    return loadFromCdn(cdnBase(PRIMER_DEFAULT_SDK_VERSION))
  })

// The npm shim can only load the version it was published as — it hardcodes its
// own PrimerCoreSDK.min.js and Checkout.css URLs — so a configured version comes
// from that version's CDN build instead. One that fails to load (a mistyped
// version fails exactly like an offline visitor's) falls back to the bundled
// version: a bad setting costs the configured version, never the checkout.
const loadConfigured = (version: string): Promise<PrimerSdk> =>
  loadFromCdn(cdnBase(version)).catch((err) => {
    console.warn(
      `[primer] SDK ${version} failed to load — falling back to ${PRIMER_DEFAULT_SDK_VERSION}`,
      err,
    )
    return loadBundled()
  })

export const loadPrimerSdk = (): Promise<PrimerSdk> => {
  if (!primerSdkPromise) {
    primerSdkPromise = (
      configuredVersion ? loadConfigured(configuredVersion) : loadBundled()
    ).catch((err) => {
      // Every path failed (still offline). Clear the singleton so the next
      // call re-attempts the whole chain.
      primerSdkPromise = null
      throw err
    })
  }
  return primerSdkPromise
}
